import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalHeader, ModalBody, Row, Col } from 'reactstrap';
import classNames from 'classnames';
import CommonButton from '../../common/components/CommonButton';
import { CommonButtonVariants } from '../../constants/CommonButtonVariants';
import SettingsSectionHeader from '../../settings/SettingsSectionHeader';
import { ServiceTypeName } from '../../constants/ServiceHistory';
import { constantToSelect } from '../../common/helpers/constantToSelect';
import '../../assets/css/componentStyles/DeleteServiceHistoryModal.css';

const serviceTypeList = constantToSelect(ServiceTypeName);

function DeleteServiceHistoryModal({
  isOpen,
  toggle,
  onConfirm,
  serviceHistoryData,
  isDeleting,
  className,
  size,
  confirmText,
  deleteButtonText = 'DELETE',
  cancelButtonText = 'CANCEL',
  onCancel,
}) {
  const handleConfirm = useCallback(() => {
    onConfirm(serviceHistoryData);
    toggle();
  }, [onConfirm, toggle, serviceHistoryData]);

  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel();
    }
    toggle();
  }, [onCancel, toggle]);

  const formattedData = useMemo(() => {
    if (!serviceHistoryData) {
      return {
        date: '',
        serviceType: '',
        serviceName: '',
        mileage: '',
      };
    }

    const formatDate = (dateString) => {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB'); // DD/MM/YYYY format
      } catch (error) {
        console.warn('Invalid date format:', dateString);
        return dateString;
      }
    };

    const getServiceTypeLabel = (value) => {
      if (!value) return '';
      const serviceType = serviceTypeList.find((type) => type.value === value);
      return serviceType ? serviceType.label : value;
    };

    const formatMileage = (mileage) => {
      if (!mileage) return '';
      const numericMileage = parseInt(mileage, 10);
      return !Number.isNaN(numericMileage) ? numericMileage.toLocaleString() : mileage;
    };
    return {
      date: formatDate(serviceHistoryData.date),
      serviceType: getServiceTypeLabel(
        serviceHistoryData.serviceType?.value || serviceHistoryData.serviceType,
      ),
      serviceName: serviceHistoryData.serviceName || '',
      mileage: formatMileage(serviceHistoryData.mileage),
    };
  }, [serviceHistoryData]);

  const modalClassName = classNames('delete-service-history-modal', className);

  const hasData = serviceHistoryData && Object.keys(serviceHistoryData).length > 0;
  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      size={size}
      backdropClassName="delete-service-history-modal-backdrop"
      className={modalClassName}
      aria-labelledby="delete-service-history-title"
      aria-describedby="delete-service-history-description"
    >
      <ModalHeader toggle={toggle} tag="div">
        <SettingsSectionHeader
          id="delete-service-history-title"
          header="Delete Service History"
          variant="noBorder"
        />
      </ModalHeader>
      <ModalBody>
        <div
          id="delete-service-history-description"
          className="confirmation-text"
          role="alert"
          aria-live="polite"
        >
          {confirmText ||
            'Please confirm that you want to delete the following service history:'}
        </div>
        {!hasData && (
          <div className="no-data-message" role="alert">
            No service history data available.
          </div>
        )}

        {hasData && (
          <>
            <Row className="mt-4">
              <Col md={6}>
                <div className="form-group--bordered-label">
                  <label className="form-input-field-label">Date</label>
                  <input
                    type="text"
                    className="form-control form-control--read-only"
                    aria-label="Service date"
                    aria-disabled="true"
                    value={formattedData.date || ''}
                    disabled
                    readOnly
                    style={{ opacity: 0.6 }}
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="form-group--bordered-label">
                  <label className="form-input-field-label">Service type</label>
                  <input
                    type="text"
                    className="form-control form-control--read-only"
                    aria-label="Service type"
                    aria-disabled="true"
                    value={formattedData.serviceType || ''}
                    disabled
                    readOnly
                    style={{ opacity: 0.6 }}
                  />
                </div>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col md={6}>
                <div className="form-group--bordered-label">
                  <label className="form-input-field-label">Service name</label>
                  <input
                    type="text"
                    className="form-control form-control--read-only"
                    aria-label="Service name"
                    aria-disabled="true"
                    value={formattedData.serviceName || ''}
                    disabled
                    readOnly
                    style={{ opacity: 0.6 }}
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="form-group--bordered-label">
                  <label className="form-input-field-label">Mileage</label>
                  <input
                    type="text"
                    className="form-control form-control--read-only"
                    aria-label="Vehicle mileage at service"
                    aria-disabled="true"
                    value={formattedData.mileage || ''}
                    disabled
                    readOnly
                    style={{ opacity: 0.6 }}
                  />
                </div>
              </Col>
            </Row>
          </>
        )}
        <div className="modal-footer-custom">
          <Row className="mt-4 mb-3">
            <Col md={6}>
              <CommonButton
                label={deleteButtonText}
                variant={CommonButtonVariants.PRIMARY_RED}
                handleClick={handleConfirm}
                disabled={isDeleting || !hasData}
                className="w-100"
                aria-label="Confirm deletion of service history"
                loading={isDeleting}
              />
            </Col>
            <Col md={6}>
              <CommonButton
                label={cancelButtonText}
                variant={CommonButtonVariants.OUTLINED_RED}
                handleClick={handleCancel}
                disabled={isDeleting}
                className="w-100"
                aria-label="Cancel deletion"
              />
            </Col>
          </Row>
        </div>
      </ModalBody>
    </Modal>
  );
}

DeleteServiceHistoryModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  serviceHistoryData: PropTypes.shape({
    date: PropTypes.string,
    serviceType: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        value: PropTypes.string,
        label: PropTypes.string,
      }),
    ]),
    serviceName: PropTypes.string,
    mileage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  isDeleting: PropTypes.bool,
  className: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'lg', 'xl']),
  confirmText: PropTypes.string,
  deleteButtonText: PropTypes.string,
  cancelButtonText: PropTypes.string,
  onCancel: PropTypes.func,
};

DeleteServiceHistoryModal.defaultProps = {
  serviceHistoryData: null,
  isDeleting: false,
  className: '',
  size: 'lg',
  confirmText: null,
  deleteButtonText: 'DELETE',
  cancelButtonText: 'CANCEL',
  onCancel: null,
};

export default DeleteServiceHistoryModal;
